import React, { useState } from 'react'
import { useEffect } from 'react';
import { ControlledAccordion, useAccordionProvider } from '@szhsin/react-accordion';
import AccordionItem, { DefaultAccordionProviderOptions } from '../AccordionItem/AccordionItem';
import { useSelector } from 'react-redux';
import { accordionDisplay, shouldDisplayPreviousTwoYearAttributes, shouldDisplayPreviousYearAttributes } from '../../../utils/previousIncomeAttributes';
import './PreviousCampaignIncomeAttributes.css'
import MenuItem from '../../../types/menu/MenuItem';
import { IncomeAttributeState } from '../../../redux/reducers/getIncomeAttributes/getPreviousIncomeAttributesReduer';
import RevenueTab from '../../../pages/PIE/Revenues/RevenueTab';
import IncomeAttributeLookupObject from '../../../types/lookups/IncomeAttributeLookupObject';
import { ReadOnlyProvider } from '../../../utils/ReadOnlyContext';
import { ShowIncomeLabelProvider } from '../../../utils/ShowIncomeLabelContext';
import ExpenseTab from '../../../pages/PIE/Expenses/ExpenseTab';
import CapitalCostsSummaryTab from '../../../pages/PIE/CapitalCostsSummaryTab';
import IncomeAttributeTableTab from '../../../pages/PIE/IncomeAttribute/IncomeAttributeTableTab';
import NumberOfSitesTab from '../../../pages/PIE/NumberOfSites/NumberOfSitesTab';
import { ProfileAttributesState } from '../../../redux/reducers/getProfile/profileAttributes/getAttributesReducer';
import RoomStatisticsTab from '../../../pages/PIE/RoomStatistics/RoomStatisticsTab';
import ReserveForReplacementAllowanceTab from '../../../pages/PIE/ReserveForReplacementAllowance/ReserveForReplacementAllowanceTab';
import MotelRevenueTab from '../../../pages/PIE/MTL/MotelRevenueTab';
import RoundsRateTab from '../../../pages/PIE/RoundsRateTab';
import CapitalCostTab from '../../../pages/PIE/CapitalCost/CapitalCostTab';
import SubsidyProfileTab from '../../../pages/PIE/Subsidy/SubsidyProfileTab';
import { selectLookups, selectPreviousIncomeAttributes, selectPreviousProfileAttributes } from '../../../redux/selectors/exportSelectorPaths';
import { FormErrorProvider } from '../../../utils/context/FormErrorContext';

interface PreviousCampaignIncomeAttributesProps {
    lookupType?: string;
    tabList?: MenuItem[];
    tabComponent: "revenue" | "expense" | "capital" | "incomeLoss" | "numberOfSites" | "roomStatistics" | "reserve" | "mtlRevenue" | "roundsRate" | "capitalTabs" | "subsidy";
    name?: string;
    lookups?: IncomeAttributeLookupObject[];
    keys?: string[];
    forcedTab?: boolean
    courses?: number[]
    year: string
}

function PreviousCampaignIncomeAttributes(props: PreviousCampaignIncomeAttributesProps) {
    const { lookupType, tabList, tabComponent, name, lookups, keys, forcedTab, courses } = props
    const previousIncomeAttributesState: IncomeAttributeState = useSelector(selectPreviousIncomeAttributes)
    const previousProfileAttributes: ProfileAttributesState = useSelector(selectPreviousProfileAttributes)
    const lookupState = useSelector(selectLookups);
    const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)
    const incomeAttributesData = previousIncomeAttributesState.data && ((previousIncomeAttributesState.data as Record<string, unknown>[]).length > 0) ? (previousIncomeAttributesState.data as Record<string, unknown>[]) : [];
    const [incomeAttributesInternalData, setIncomeAttributesInternalData] = useState([])
    const profileAttributesData = previousProfileAttributes.data
    const [profileAttributesInternalData, setProfileAttributesInternalData] = useState([])
    const [header, setHeader] = useState("")
    const year = props.year
    const isInternalUser = sessionStorage.getItem("isInternalUser") ? true : false;

    useEffect(() => {
        if (!isInternalUser) {
            const sessionStoragePreviousSnapshotData = sessionStorage.getItem("previousSnapshotData")
            if (isInternalUser && sessionStoragePreviousSnapshotData !== null && sessionStoragePreviousSnapshotData !== undefined) {
                let previousSnapshotData = { campaign: { analysisFlag: "" } }
                previousSnapshotData = JSON.parse(sessionStoragePreviousSnapshotData)
                const analysisFlag = previousSnapshotData?.campaign?.analysisFlag
                if (analysisFlag) {
                    setHeader(year + " (Analysis = " + analysisFlag + ")")
                    return
                }
            }
        } else {
            const sessionStoragePreviousSnapshotDataInternal = sessionStorage.getItem("previousSnapshotDataInternal" + year)
            if (sessionStoragePreviousSnapshotDataInternal !== null && sessionStoragePreviousSnapshotDataInternal !== undefined) {
                let previousSnapshotData = { campaign: { analysisFlag: "" }, incomeAttribute: [], profileAttributes: [] }
                previousSnapshotData = JSON.parse(sessionStoragePreviousSnapshotDataInternal)
                setIncomeAttributesInternalData(previousSnapshotData?.incomeAttribute)
                setProfileAttributesInternalData(previousSnapshotData?.profileAttributes)
                const analysisFlag = previousSnapshotData?.campaign?.analysisFlag
                if (analysisFlag) {
                    setHeader(year + " (Analysis = " + analysisFlag + ")")
                    return
                }
            }
        }
        setHeader(year)
    }, [year, isInternalUser])

    // Used to pass ESLint empty arrow function error
    const noOp = () => { console.log("unsupported action") }

    const generateTabComponent = (tab: MenuItem | null) => {
        switch (tabComponent) {
            case "revenue":
                return <RevenueTab handleTotalIncome={noOp}
                    key={tab ? tab.name : name}
                    handleTotalLoss={noOp}
                    handleCommentChange={noOp}
                    name={tab ? tab.name : (name as string)}
                    lookups={lookupState.lookups[1] ? (lookupState.lookups[1] as Record<string, IncomeAttributeLookupObject[]>)[tab ? tab.name : name as string] : []}
                    incomeAttributes={isInternalUser ? incomeAttributesInternalData : incomeAttributesData}
                    lookupType={lookupType as string} />
            case "expense":
                return <ExpenseTab
                    lookups={lookupState.lookups[2] ? (lookupState.lookups[2] as Record<string, IncomeAttributeLookupObject[]>)[tab ? tab.name : name as string] : []}
                    handleCommentChange={noOp}
                    handleTotalExpense={noOp}
                    incomeAttributes={isInternalUser ? incomeAttributesInternalData : incomeAttributesData} />
            case "capital":
                return <CapitalCostsSummaryTab
                    lookupType={lookupType as string}
                    handleCommentChange={noOp}
                    handleCurrencyChange={noOp}
                    incomeAttributes={isInternalUser ? incomeAttributesInternalData : incomeAttributesData}
                    lookups={lookupState.lookups[3]["cost"] ? (lookupState.lookups[3]["cost"] as IncomeAttributeLookupObject[]) : []}
                    name={String(name)} />
            case "incomeLoss":
                return <IncomeAttributeTableTab
                    incomeAttributes={isInternalUser ? incomeAttributesInternalData : incomeAttributesData}
                    handleCommentChange={noOp}
                    handleCurrencyChange={noOp}
                    lookups={lookups as IncomeAttributeLookupObject[]} />
            case "numberOfSites":
                return <NumberOfSitesTab
                    keys={keys as string[]}
                    data={(isInternalUser ? profileAttributesInternalData : profileAttributesData) as Record<string, number | null>}
                    valueChangeHandler={noOp} />
            case "roomStatistics":
                return <RoomStatisticsTab
                    valueChangeHandler={noOp}
                    data={(isInternalUser ? profileAttributesInternalData : profileAttributesData) as Record<string, number | null>}
                    lookups={lookups as IncomeAttributeLookupObject[]} />
            case "reserve":
                return <ReserveForReplacementAllowanceTab
                    incomeAttributes={isInternalUser ? incomeAttributesInternalData : incomeAttributesData}
                    lookups={lookups as IncomeAttributeLookupObject[]}
                    handleValueChange={noOp}
                    handleCommentChange={noOp} />
            case "mtlRevenue": {
                const tabLookupKey = tab ? (tab.name === "nonOperatingIncomeRevenues" ? "nonOperatingIncomeRevenues" : "motelRevenue") : name as string
                return <MotelRevenueTab
                    incomeAttributes={isInternalUser ? incomeAttributesInternalData : incomeAttributesData}
                    lookups={lookupState.lookups[1] ? (lookupState.lookups[1] as Record<string, IncomeAttributeLookupObject[]>)[tabLookupKey] : []}
                    handleCommentChange={noOp}
                    valueChangeHandler={noOp} />
            }
            case "roundsRate":
                return <ControlledAccordion providerValue={providerValue}>
                    <RoundsRateTab
                        data={(isInternalUser ? profileAttributesInternalData : profileAttributesData) as Record<string, number | null>}
                        keys={keys as string[]}
                        courses={courses as number[]}
                        valueChangeHandler={noOp}
                        previousCampaign={true}
                        year={year} />
                </ControlledAccordion>
            case "capitalTabs":
                return <CapitalCostTab
                    lookups={lookupState.lookups[3] ? (lookupState.lookups[3] as Record<string, IncomeAttributeLookupObject[]>)[tab ? tab.name : name as string] : []}
                    name={tab ? tab.name : (name as string)}
                    incomeAttributes={isInternalUser ? incomeAttributesInternalData : incomeAttributesData}
                    valueChangeHandler={noOp}
                    commentChangeHandler={noOp} />
            case "subsidy":
                return <ControlledAccordion providerValue={providerValue}>
                    <AccordionItem
                        header={header}
                        key={`${year}-data`}
                        itemKey={`${year}-data`}>
                        <SubsidyProfileTab
                            data={(isInternalUser ? profileAttributesInternalData : profileAttributesData) as Record<string, number | null>}
                            keys={keys as string[]}
                            handleCommentChange={noOp}
                            handleCurrencyChange={noOp}
                            valueChangeHandler={noOp} />
                        <AccordionItem
                            className={"invisible-accordion"}
                            header={header}
                            key={"subsidy-income"}
                            itemKey={"subsidy-income"}>
                            <RevenueTab
                                name='revenue'
                                lookups={lookupState.lookups[1] ? (lookupState.lookups[1] as Record<string, IncomeAttributeLookupObject[]>)["revenue"] : []}
                                incomeAttributes={isInternalUser ? incomeAttributesInternalData : incomeAttributesData}
                                lookupType={lookupType as string}
                                handleCommentChange={noOp}
                                handleTotalIncome={noOp} />
                        </AccordionItem>
                        <AccordionItem
                            className={"invisible-accordion"}
                            header={header}
                            key={"subsidy-income-nonOperatingIncomeRevenues"}
                            itemKey={"subsidy-income-nonOperatingIncomeRevenues"}>
                            <RevenueTab
                                name='nonOperatingIncomeRevenues'
                                lookups={lookupState.lookups[1] ? (lookupState.lookups[1] as Record<string, IncomeAttributeLookupObject[]>)["nonOperatingIncomeRevenues"] : []}
                                incomeAttributes={isInternalUser ? incomeAttributesInternalData : incomeAttributesData}
                                lookupType={lookupType as string}
                                handleCommentChange={noOp}
                                handleTotalIncome={noOp} />
                        </AccordionItem>

                        <AccordionItem
                            className={"invisible-accordion"}
                            header={header}
                            key={"subsidy-income-rentalLoss"}
                            itemKey={"subsidy-income-rentalLoss"}>
                            <RevenueTab
                                name='rentalLoss'
                                lookups={lookupState.lookups[1] ? (lookupState.lookups[1] as Record<string, IncomeAttributeLookupObject[]>)["rentalLoss"] : []}
                                incomeAttributes={isInternalUser ? incomeAttributesInternalData : incomeAttributesData}
                                lookupType={lookupType as string}
                                handleCommentChange={noOp}
                                handleTotalIncome={noOp} />
                        </AccordionItem>
                    </AccordionItem>
                </ControlledAccordion>
        }
    }

    return (
        <>
            {(shouldDisplayPreviousYearAttributes() || shouldDisplayPreviousTwoYearAttributes()) &&
                <ShowIncomeLabelProvider initialState={false}>
                    <ReadOnlyProvider isInitiallyReadOnly={true}>
                        <FormErrorProvider>
                            <div className={accordionDisplay(isInternalUser, "right")}>
                                {tabList
                                    ?
                                    <ControlledAccordion providerValue={providerValue}>
                                        <AccordionItem
                                            header={header}
                                            key={`${year}-data`}
                                            itemKey={`${year}-data`}>
                                            {tabList.map((tab, index) => {
                                                if (index === 0) {
                                                    return generateTabComponent(tab)
                                                } else {
                                                    return <AccordionItem
                                                        className={"invisible-accordion"}
                                                        header={header}
                                                        key={tab.name + "-" + index}
                                                        itemKey={tab.name + "-" + index}>
                                                        {generateTabComponent(tab)}
                                                    </AccordionItem>
                                                }
                                            })}
                                        </AccordionItem>
                                    </ControlledAccordion>
                                    :
                                    <>
                                        {forcedTab ?
                                            <ControlledAccordion providerValue={providerValue}>
                                                <AccordionItem header={header}
                                                    key={`${year}-data`}
                                                    itemKey={`${year}-data`}>
                                                    {generateTabComponent(null)}
                                                </AccordionItem>
                                            </ControlledAccordion>
                                            : generateTabComponent(null)
                                        }
                                    </>
                                }
                            </div>
                        </FormErrorProvider>
                    </ReadOnlyProvider>
                </ShowIncomeLabelProvider>
            }
        </>
    )
}

export default PreviousCampaignIncomeAttributes